import t from 'react-translate';
import { Row, Table } from '@tanstack/react-table';
import React, { useContext, useEffect, useMemo } from 'react';
import { MentoringProgramRole, MentoringProgramEnrollment } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramEnrollmentsList } from 'redux/selectors/mentorship-program-enrollments';
import { useAppDispatch } from 'redux/store';
import NvTable, { Column, RowProps } from 'shared/components/nv-table';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import { getMentoringProgramEnrollments } from 'redux/actions/mentoring-program-enrollments';
import { useInView } from 'react-intersection-observer';
import NvNoResults from 'shared/components/nv-no-results-panel';
import { css } from '@emotion/react';
import { tripleSpacing } from 'styles/global_defaults/scaffolding';
import { ColumnID } from '../../connections/participant-row/types';
import MentoringProgramContext from '../../context';
import { PAGE_SIZE, PageSizeParams } from '../../constants';
import LoadingRow from '../../loading-row';
import EmptyParticipants from '../../participants/empty-participants';

const fallbackData = [];

const emptyStyles = css`
  margin-top: 100px;
`;

type Params = PageSizeParams & {
  textSearch: string;
  courseRoleName: MentoringProgramRole;
};

type ParticipantsListProps = {
  listRef: React.Ref<Table<MentoringProgramEnrollment>>;
  listType: MentoringProgramRole;
  tableHeader: string;
  rowType: React.ComponentType<RowProps<MentoringProgramEnrollment>>;
  enableMultiSelection: boolean;
  textSearch?: string;
  enableRowSelection?: (row: Row<MentoringProgramEnrollment>) => boolean;
  filterRows?: (enrollment: MentoringProgramEnrollment) => boolean;
  setTextSearch: (newTextSearch: string) => void;
};

const ParticipantsList = React.memo(
  ({
    listRef,
    listType,
    tableHeader,
    rowType,
    textSearch,
    enableMultiSelection,
    enableRowSelection,
    filterRows,
    setTextSearch,
  }: ParticipantsListProps) => {
    const dispatch = useAppDispatch();
    const {
      mentoringProgram: { id: programId },
    } = useContext(MentoringProgramContext);

    const { ref: scrollRef, inView: reachedEnd } = useInView();

    const tableColumns: Column<MentoringProgramEnrollment>[] = [
      {
        id: ColumnID.SELECTOR,
        header: tableHeader,
      },
      {
        id: ColumnID.PARTICIPANT,
      },
    ];

    const params = useMemo(
      () => ({
        pageSize: PAGE_SIZE,
        textSearch: textSearch?.trim() || undefined,
        courseRoleName: listType,
        isMatched: listType === MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE ? false : undefined,
        updateCounts: false,
      }),
      [textSearch],
    );

    const bodyProps = {
      ref: scrollRef,
      className: 'table-body',
    };

    const { result, loadMore, isLoading } = usePaginatedApi<
    MentoringProgramEnrollment,
    Params
    >(
      p => dispatch(
        getMentoringProgramEnrollments({
          programId,
          ...p,
        }),
      )
        .then(action => action.payload)
        .then((enrollments: MentoringProgramEnrollment[]) => {
          if (filterRows) return (enrollments || []).filter(filterRows);
          return enrollments;
        }),
      params,
      getMentoringProgramEnrollmentsList,
    );

    useEffect(() => {
      if (reachedEnd) {
        loadMore();
      }
    }, [loadMore, reachedEnd]);

    return (
      <NvTable<MentoringProgramEnrollment>
        data={result ?? fallbackData}
        tableRef={listRef}
        RowComponent={rowType}
        bodyProps={bodyProps}
        columns={tableColumns}
        enableRowSelection={enableRowSelection}
        enableMultiRowSelection={enableMultiSelection}
        renderAfterRows={(afterRowsProps) => {
          if (result != null && !result.length) {
            return (textSearch ? (
              <NvNoResults
                hideClearSearch={false}
                style={{ marginTop: `${tripleSpacing}px` }}
                action={() => setTextSearch('')}
                clearText={t.SEARCH.CLEAR()}
                noResultsText={t.SEARCH.NO_RESULTS_FOUND()}
              />
            ) : (
              <EmptyParticipants css={emptyStyles} role={listType} />
            ));
          }
          return isLoading && <LoadingRow {...afterRowsProps} />;
        }}
      />
    );
  },
);

export default ParticipantsList;
